import type { BrandedIconProps } from './types';

/**
 * Elo — Brazilian card network (Boticario / Bradesco / Banco do Brasil
 * / Caixa joint venture, launched 2011). The brand mark is three
 * overlapping arc segments arranged on a black rounded rectangle:
 *
 *   - Top-left arc:    `#FFCB05` (Elo yellow)
 *   - Bottom arc:      `#00A4E0` (Elo cyan / blue)
 *   - Right arc:       `#EF4123` (Elo red)
 *
 * Rationale for a card-shaped mark
 * --------------------------------
 * Like Visa/Mastercard, the bare Elo rosette is hard to read below ~32px,
 * and Brazilian users instinctively associate Elo with the black card
 * background that ships on every co-branded card. We render the
 * rounded-rect "card" container with the three colored arcs and the "elo"
 * wordmark — same composition Elo itself uses on its checkout assets at
 * https://www.elo.com.br/sou-portador.
 *
 * Colors per Elo Brand Guidelines (https://www.elo.com.br):
 *  - `#FFCB05` Elo yellow
 *  - `#00A4E0` Elo cyan
 *  - `#EF4123` Elo red
 *  - `#000000` card background
 *  - `#FFFFFF` wordmark
 *
 * Mark adapted from the public Elo brand assets. DO NOT recolor — see
 * `BrandedIconProps` for the rationale.
 */
export function EloIcon({
  size = 24,
  className,
  'aria-label': ariaLabel = 'Elo',
}: BrandedIconProps): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      {/* Card body — Elo's signature black */}
      <rect x="1" y="5" width="22" height="14" rx="2.5" fill="#000000" />

      {/* Rosette — three overlapping circular wedges centered at (7.5, 12).
          Each wedge spans roughly 120deg, in the order yellow → red → cyan
          rotating clockwise from the top-left. Drawn as filled annular
          sectors so the three pieces interlock without overlap, matching
          the official Elo mark. */}
      {/* Yellow wedge — top-left */}
      <path fill="#FFCB05" d="M7.5 8.4a3.6 3.6 0 0 0-3.5 2.78l1.46.34A2.1 2.1 0 0 1 7.5 9.9V8.4Z" />
      {/* Red wedge — top-right */}
      <path
        fill="#EF4123"
        d="M7.5 8.4v1.5a2.1 2.1 0 0 1 2.04 1.62l1.46-.34A3.6 3.6 0 0 0 7.5 8.4Z"
      />
      {/* Cyan wedge — bottom */}
      <path
        fill="#00A4E0"
        d="M4 11.18a3.6 3.6 0 0 0 7 1.64l-1.46-.34a2.1 2.1 0 0 1-4.08 0L4 11.18Z"
      />
      {/* Inner dot — black center to give the rosette its open ring shape */}
      <circle cx="7.5" cy="12" r="1.1" fill="#000000" />

      {/* "elo" wordmark — Elo white, optimized for legibility at 24px.
          Lower-case sans-serif glyphs sit to the right of the rosette. */}
      <path
        fill="#FFFFFF"
        d="M14.5 13.05c.1.42.46.7.96.7.36 0 .68-.13.92-.38l.7.62c-.4.43-.94.66-1.62.66-1.16 0-2.02-.78-2.02-1.95s.84-1.95 1.94-1.95c1.06 0 1.86.74 1.86 1.93 0 .1-.01.21-.02.37h-2.72Zm-.02-.74h1.78c-.08-.4-.4-.66-.86-.66-.46 0-.82.26-.92.66Zm3.74-2.66h1.04v4.94h-1.04v-4.94Zm3.5 1.04c1.14 0 2 .82 2 1.94 0 1.13-.86 1.95-2 1.95s-2-.82-2-1.95c0-1.12.86-1.94 2-1.94Zm0 .92c-.55 0-.96.42-.96 1.02s.41 1.02.96 1.02.96-.42.96-1.02-.41-1.02-.96-1.02Z"
      />
    </svg>
  );
}
